import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Scrolls to the element named by the URL hash once the list has rendered.
 *
 * The browser resolves a hash while the document loads, which is long before
 * this list has its accounts, so the native behaviour finds nothing. Waiting
 * for `isReady` and scrolling by hand is what makes a link like
 * `/depots-accounts#account-42` land on the right row.
 */
export const useScrollToHashedRow = (isReady: boolean) => {
  const { hash } = useLocation();
  const scrolledToRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isReady || !hash) return;

    const elementId = hash.slice(1);
    // Scroll once per target, so re-renders don't yank a reading user back.
    if (scrolledToRef.current === elementId) return;

    const element = document.getElementById(elementId);
    if (!element) return;

    scrolledToRef.current = elementId;

    // Deferred by a frame on purpose. With the accounts already cached the list
    // is there on the first render after navigating, so this effect and
    // ScrollToTop's `scrollTo(0, 0)` run in the same commit — and ScrollToTop
    // runs last, undoing the scroll while the ref blocks a second attempt. A
    // frame later every effect of that commit has run and this scroll holds.
    const frame = requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => cancelAnimationFrame(frame);
  }, [isReady, hash]);
};
